import { useContext, Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useDisclosure, useDebouncedValue, useMediaQuery } from '@mantine/hooks';

import { HeadquarterContext } from 'context/HeadquarterContext';

import { PRICING_PLANS } from 'constants/chownow';
import { LOCATION_PATH } from 'constants/path';

import { Button, Divider, Group, Indicator, Modal, Text, TextInput } from '@mantine/core';
import RestaurantProgress from 'components/RestaurantProgress';

import { breakpoints } from 'styles/theme.variables';
import { useGetLocationHomePath } from 'hooks/useGetLocationHomePath';
import { getUpdatedLocationPath } from 'scopes/nav';
import { STATUSES } from 'hooks/useLocation';
import CardEmpty from 'components/CardEmpty/CardEmpty';

import { ChevronDownIconXs, CloseIconSm, GlobeIconMd, GlobeIconSm, RestaurantIconMd, RestaurantIconSm, SearchIconSm } from '@chownow/pantry/icons';
export default function LocationsSelector({ toggleNav }) {
  const { headquarterLocations, location, locationStatus } =
    useContext(HeadquarterContext);
  const getLocationHomePath = useGetLocationHomePath();

  const [locationsModalOpened, locationsModalHandlers] = useDisclosure(false);
  const [searchQuery, setSearchQuery] = useState();
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery, 200);
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.maxMobile})`);

  const { pathname } = useLocation();
  const params = useParams();
  const navigate = useNavigate();

  const isHqLevelPage = !pathname.includes(LOCATION_PATH);

  const lowerDebouncedSearchQuery = debouncedSearchQuery?.toLowerCase();
  const filteredList = headquarterLocations.filter(loc => {
    if (!lowerDebouncedSearchQuery) {
      return true;
    }

    const includesName = loc.name.toLowerCase().includes(lowerDebouncedSearchQuery);
    const includesAddress = loc.address.street_address1
      .toLowerCase()
      .includes(lowerDebouncedSearchQuery);

    return includesName || includesAddress;
  });

  function handleLocationSelection(id) {
    locationsModalHandlers.close();

    if (id) {
      if (isHqLevelPage) {
        navigate(getLocationHomePath(id));
      } else {
        navigate(getUpdatedLocationPath({ pathname, id, prevId: params.restaurantId }));
      }
    } else {
      navigate('/');
    }
  }

  let selectorText;

  if (isHqLevelPage) {
    selectorText = 'Headquarters';
  } else if (
    locationStatus === STATUSES.UNAUTHORIZED ||
    (!location && locationStatus === STATUSES.ERRORED)
  ) {
    selectorText = 'Select Location';
  } else {
    selectorText = location?.locale_name || location?.name;
  }

  return (
    <>
      <Button
        onClick={() => {
          if (isMobile) toggleNav();
          locationsModalHandlers.open();
        }}
        variant="ghost-navigation"
        leftSection={isHqLevelPage ? <GlobeIconSm /> : <RestaurantIconSm />}
        rightSection={<ChevronDownIconXs />}
        fullWidth
        justify="space-between"
        className="locationSelector"
        styles={{
          root: {
            borderTop: isMobile ? '1px solid var(--border-secondary)' : 'none',
            marginTop: isMobile && 'var(--mantine-spacing-md)',
          },
          label: { flex: 1 },
        }}
      >
        <Text truncate="end">{selectorText}</Text>
      </Button>
      <Modal
        opened={locationsModalOpened}
        onClose={locationsModalHandlers.close}
        title={
          <>
            <Text variant="caption-lg">Switch Restaurants</Text>
            <TextInput
              value={searchQuery || ''}
              leftSection={<SearchIconSm />}
              placeholder="Search"
              rightSectionPointerEvents="all"
              rightSection={
                <CloseIconSm
                  aria-label="Clear input"
                  onClick={() => setSearchQuery('')}
                  style={{ display: searchQuery ? undefined : 'none' }}
                />
              }
              onChange={event => setSearchQuery(event.currentTarget.value)}
              size="md"
              mt="sm"
              mr="calc(var(--mantine-spacing-md) * -1)"
            />
          </>
        }
        size={isMobile ? 'sm' : 'lg'}
        styles={{
          content: { height: filteredList?.length ? '720px' : 'auto' },
          title: { flex: 1 },
        }}
      >
        {filteredList?.length ? (
          <>
            <Button
              variant="menu-item"
              onClick={() => handleLocationSelection()}
              leftSection={!isMobile && <GlobeIconMd />}
              mb="xs"
              size={isMobile ? 'sm' : 'md'}
            >
              <Text variant="label-md">Headquarters</Text>
            </Button>
            {filteredList.map(loc => (
              <Fragment key={loc.id}>
                <Divider />
                <Button
                  variant="menu-item"
                  onClick={() => handleLocationSelection(loc.id)}
                  size={isMobile ? 'sm' : 'md'}
                  leftSection={!isMobile && <RestaurantIconMd />}
                  rightSection={
                    <Group gap="xs2">
                      <RestaurantProgress location={loc} compact />

                      <Indicator
                        label={loc.product_plan_group}
                        variant={
                          loc.product_plan_group === PRICING_PLANS.noPlan ? 'neutral' : 'active'
                        }
                        size="16"
                      />
                    </Group>
                  }
                  my="xs"
                  h="var(--mantine-spacing-xl2)"
                >
                  <Text truncate="end" variant="label-md" style={{ maxWidth: '100%' }}>
                    {loc.name || loc.locale_name || loc.address?.city}
                  </Text>
                </Button>
              </Fragment>
            ))}
          </>
        ) : (
          <CardEmpty />
        )}
      </Modal>
    </>
  );
}

LocationsSelector.propTypes = {
  toggleNav: PropTypes.func,
};
